import BirlExerciseWeightMap from 'shared/birl/exercises/WeightMap'
import BirlExerciseEntriesIndex from 'shared/birl/exercise_entries/Index'
import DodoneContext from 'app/dodones/Context'
import DodoneFinishButton from 'app/dodones/shared/FinishButton'
import DodoneNextLink from 'app/dodones/shared/NextLink'
import DodoneStartButton from 'app/dodones/shared/StartButton'
import DodonesList from 'app/dodones/list/Index'
import HeaderContext from 'app/HeaderContext'
import LoadingScreen from 'app/components/LoadingScreen'
import NumberPicker from 'app/inputs/NumberPicker'

import update from 'immutability-helper'
import useBirlExerciseQuery from 'generated/api/birl/exercises/useBirlExerciseQuery'
import { format } from 'date-fns'
import RestTimer from 'app/components/RestTimer'
import DodoneShowGenericWrapper from 'app/dodones/generic/show/Wrapper'
import DodoneGenericShowContext from 'app/dodones/generic/show/Context'

const StartedBirlExercise = ({
  dodone,
  birlExercise,
  series,
  setSeries,
  setSerieReps
}) => {
  const up = useCallback((e, props) => {
    const index = props.index
    setSerieReps(index, series[index].reps + 1)
  })

  const down = useCallback((e, props) => {
    const index = props.index
    setSerieReps(index, Math.max(series[index].reps - 1, 0))
  })

  const addSerie = useCallback(() => {
    const updatedSeries = update(series, {
      $push: [series[series.length - 1]]
    })

    setSeries(updatedSeries)
  })

  const removeSerie = useCallback((index) => {
    const updatedSeries = update(series, {
      $splice: [[index, 1]]
    })

    setSeries(updatedSeries)
  })

  return (
    <div className="frow gap-1 mb-d">
      {Object.entries(series).map(([index, serie]) => {
        const weightDisplay = isNaN(serie.weight) ? '?' : `${serie.weight}kg`

        return (
          <div key={index} className="flex-1 fcol">
            <NumberPicker up={up} down={down} index={index}>
              <div className="gray-transparent-box pt-2 rounded">
                <div>{weightDisplay} </div>
                <div className="frow items-center gap-2 justify-center">
                  <div className="text-2xl">{serie.reps}</div>
                  reps
                </div>
              </div>
            </NumberPicker>
          </div>
        )
      })}
    </div>
  )
}

export default () => {
  const { setText } = useContext(HeaderContext)
  const { dodone, updateDodone } = useContext(DodoneContext)
  const { isPending, data: { birlExercise } = {} } = useBirlExerciseQuery({
    birlExerciseId: dodone.dodoable.settings.exerciseId
  })
  const [series, setSeries] = useState([])
  const [serieSetAt, setSerieSetAt] = useState(new Date())

  useEffect(() => {
    setText(dodone.dodoable.name)
    setSerieSetAt(dodone.updatedAt)
  }, [dodone.id])

  const setSerieReps = useCallback((index, n) => {
    const updatedSeries = update(series, { [index]: { reps: { $set: n } } })
    setSeries(updatedSeries)
    setSerieSetAt(new Date()) // Legacy: Auto Chronometer
    const updatedLog = update(dodone.log, { series: { $set: updatedSeries } })
    updateDodone.mutate({ attributes: { log: updatedLog }, skipRefetch: true })
  })

  useEffect(() => {
    if (!birlExercise?.weightMap || !dodone?.log || !dodone?.log?.series) return

    setSeries(
      dodone.log.series.map((serie) => {
        return {
          ...serie,
          weight: birlExercise.weightMap[serie.weightLabel - 1].weight || 0
        }
      })
    )
  }, [dodone?.log, birlExercise?.weightMap])

  const weightSignature = useMemo(() => {
    if (!birlExercise?.weightMap) return null

    return (dodone.log.series || [])
      .map((serie) => {
        const weight = birlExercise.weightMap[serie.weightLabel + 1]?.weight
        return isNaN(weight) ? '' : weight
      })
      .join('/')
  }, [dodone, birlExercise])

  const showStartButton =
    !dodone.isStarted && birlExercise && !birlExercise.isMissingWeightMap

  const showFinishButton = dodone.isStarted && !dodone.isFinished

  if (isPending) return <LoadingScreen />

  return (
    <DodoneGenericShowContext.Provider
      value={{ showCommentFieldTrigger: false }}
    >
      <DodoneShowGenericWrapper
        chronometerParameters={{ hide: true, start: serieSetAt }}
      >
        <div className="mb-d">
          <RestTimer />
        </div>
        <BirlExerciseWeightMap birlExercise={birlExercise} />
        <BirlExerciseEntriesIndex
          exerciseId={dodone.dodoable.settings.exerciseId}
          weightSignature={weightSignature}
        />
        {!birlExercise.isMissingWeightMap && (
          <div className="flex-1 fcol">
            {dodone.isStarted && (
              <StartedBirlExercise
                dodone={dodone}
                birlExercise={birlExercise}
                series={series}
                setSeries={setSeries}
                setSerieReps={setSerieReps}
              />
            )}
          </div>
        )}
      </DodoneShowGenericWrapper>
    </DodoneGenericShowContext.Provider>
  )
}
